import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  PRINT_REQUEST_PAGE_SIZE,
  approvePrintRequest,
  createPrintRequest,
  deletePrintRequest,
  getPrintBooks,
  getPrintRequests,
  unapprovePrintRequest,
  updatePrintRequest,
} from '../services/print_service'
import './PrintRequest.css'

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getRequestId = (request) =>
  getValue(
    request,
    ['print_requestid', 'printRequestId', 'requestId', 'id'],
    '',
  )
const getBookId = (book) =>
  getValue(book, ['bookid', 'bookId', 'book_id', 'id'], '')
const getBookTitle = (record) => {
  const book = getValue(
    record,
    ['book_title', 'bookTitle', 'book', 'title', 'name'],
    'Untitled book',
  )
  if (typeof book !== 'object' || book === null) return book
  return getValue(
    book,
    ['book_title', 'bookTitle', 'title', 'name', 'description'],
    'Untitled book',
  )
}
const getRequesterName = (request) => {
  const requester = getValue(
    request,
    ['requesting_user', 'requested_by', 'requester', 'requestBy'],
    '-',
  )
  if (typeof requester !== 'object' || requester === null) return requester
  const requesterName = getValue(
    requester,
    ['user_fullname', 'full_name', 'fullName', 'fullname', 'name', 'email'],
    '',
  )
  if (requesterName) return requesterName
  return getValue(
    requester.user,
    ['user_fullname', 'full_name', 'fullName', 'fullname', 'name', 'email'],
    '-',
  )
}
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}

function PrintRequestModal({ request, userId, onClose, onSaved }) {
  const isEdit = Boolean(request)
  const [form, setForm] = useState({
    book_type: getValue(request, ['book_type', 'bookType'], 'published_book'),
    bookid: String(getValue(request, ['bookid', 'book_id'], '')),
    quantity: getValue(request, ['qty', 'quantity'], ''),
    requesting_comment: getValue(
      request,
      ['requesting_comment', 'comment', 'remarks'],
      '',
    ),
  })
  const [books, setBooks] = useState([])
  const [isLoadingBooks, setIsLoadingBooks] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setIsLoadingBooks(true)
    setBooks([])
    setError('')
    getPrintBooks(form.book_type, controller.signal)
      .then(setBooks)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingBooks(false)
      })
    return () => controller.abort()
  }, [form.book_type])

  const updateField = (event) => {
    const { name, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'book_type' ? { bookid: '' } : {}),
    }))
  }
  const submit = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError('')
    try {
      const payload = {
        bookid: form.bookid,
        quantity: Number(form.quantity),
        requesting_comment: form.requesting_comment,
        userid: userId,
      }
      if (isEdit) await updatePrintRequest(getRequestId(request), payload)
      else await createPrintRequest(payload)
      onSaved()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  const selectedBookExists = books.some(
    (book) => String(getBookId(book)) === form.bookid,
  )

  return (
    <div
      className='print-modal__backdrop'
      role='presentation'
      onMouseDown={(event) =>
        event.target === event.currentTarget && !isSaving && onClose()
      }
    >
      <section
        className='print-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='print-form-title'
      >
        <div className='print-modal__header'>
          <div>
            <span className='app-content__eyebrow'>Print workflow</span>
            <h2 id='print-form-title'>
              {isEdit ? 'Edit print request' : 'New print request'}
            </h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            disabled={isSaving}
            aria-label='Close print request form'
          >
            x
          </button>
        </div>
        {error && (
          <div className='print-page__error' role='alert'>
            {error}
          </div>
        )}
        <form className='print-form' onSubmit={submit}>
          <label className='print-form__full'>
            Book type
            <select
              name='book_type'
              value={form.book_type}
              onChange={updateField}
            >
              <option value='published_book'>Published books</option>
              <option value='exercise_book'>Exercise books</option>
            </select>
          </label>
          <label className='print-form__full'>
            Book
            <select
              name='bookid'
              value={form.bookid}
              onChange={updateField}
              required
              disabled={isLoadingBooks}
            >
              <option value=''>
                {isLoadingBooks ? 'Loading books...' : 'Select a book'}
              </option>
              {isEdit && form.bookid && !selectedBookExists && (
                <option value={form.bookid}>{getBookTitle(request)}</option>
              )}
              {books.map((book) => (
                <option key={getBookId(book)} value={String(getBookId(book))}>
                  {getBookTitle(book)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Quantity
            <input
              name='quantity'
              type='number'
              min='1'
              step='1'
              value={form.quantity}
              onChange={updateField}
              required
            />
          </label>
          <label className='print-form__full'>
            Comment
            <textarea
              name='requesting_comment'
              rows='4'
              value={form.requesting_comment}
              onChange={updateField}
              placeholder='Optional printing notes'
            />
          </label>
          <div className='print-modal__actions'>
            <button type='button' onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className='print-button--primary'
              type='submit'
              disabled={isSaving || isLoadingBooks}
            >
              {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Add request'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function PrintRequest() {
  const { user, hasPermission } = useAuth()
  const canAddPrint = hasPermission('addPrint')
  const canEditPrint = hasPermission('editPrint')
  const canDeletePrint = hasPermission('deletePrint')
  const canApprovePrint = hasPermission('approvePrint')
  const canUnapprovePrint = hasPermission('unapprovePrint')
  const userId = user?.userid ?? user?.user_id ?? user?.id
  const [requests, setRequests] = useState([])
  const [search, setSearch] = useState('')
  const [currentCursor, setCurrentCursor] = useState(null)
  const [nextCursor, setNextCursor] = useState(null)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isUnapproving, setIsUnapproving] = useState(false)
  const [modal, setModal] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingApproval, setPendingApproval] = useState(null)
  const [approvalDate, setApprovalDate] = useState('')
  const [approvalComment, setApprovalComment] = useState('')
  const [pendingUnapproval, setPendingUnapproval] = useState(null)
  const [error, setError] = useState('')

  const loadRequests = async (cursor = null, query = search, signal) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getPrintRequests({ cursor, search: query, signal })
      if (signal?.aborted) return
      setRequests(result.requests)
      setCurrentCursor(cursor)
      setNextCursor(result.nextCursor)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') setError(requestError.message)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(
      () => {
        setCursorHistory([])
        loadRequests(null, search, controller.signal)
      },
      search ? 350 : 0,
    )
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const goNext = () => {
    if (!nextCursor) return
    setCursorHistory((history) => [...history, currentCursor])
    loadRequests(nextCursor)
  }
  const goPrevious = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previousCursor = history.pop()
    setCursorHistory(history)
    loadRequests(previousCursor)
  }
  const refresh = async () => {
    setModal(null)
    setCursorHistory([])
    await loadRequests(null)
  }
  const confirmDelete = async () => {
    setIsDeleting(true)
    setError('')
    try {
      await deletePrintRequest(getRequestId(pendingDelete))
      setPendingDelete(null)
      await loadRequests(currentCursor)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeleting(false)
    }
  }
  const confirmApproval = async () => {
    if (!pendingApproval) return
    setIsApproving(true)
    setError('')
    try {
      await approvePrintRequest(getRequestId(pendingApproval), {
        approved_date: approvalDate,
        approval_comment: approvalComment.trim(),
        userid: userId,
      })
      setPendingApproval(null)
      setApprovalDate('')
      setApprovalComment('')
      await loadRequests(currentCursor)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsApproving(false)
    }
  }
  const confirmUnapproval = async () => {
    if (!pendingUnapproval) return
    setIsUnapproving(true)
    setError('')
    try {
      await unapprovePrintRequest(getRequestId(pendingUnapproval))
      setPendingUnapproval(null)
      await loadRequests(currentCursor)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsUnapproving(false)
    }
  }

  return (
    <div className='print-page'>
      <nav className='print-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Print Requests</strong>
      </nav>
      <header className='print-page__header'>
        <div>
          <span className='app-content__eyebrow'>Production</span>
          <h1>Print requests</h1>
          <p>
            Track book printing quantities, requesters, and production status.
          </p>
        </div>
        {canAddPrint && (
          <button
            className='print-button print-button--primary'
            type='button'
            onClick={() => setModal({ mode: 'add' })}
          >
            + Add request
          </button>
        )}
      </header>
      {error && (
        <div className='print-page__error' role='alert'>
          {error}
        </div>
      )}
      <section className='print-table-card' aria-labelledby='print-list-title'>
        <div className='print-table-card__header'>
          <div>
            <h2 id='print-list-title'>Request register</h2>
            <p>{requests.length} requests on this page</p>
          </div>
        </div>
        <div className='print-toolbar'>
          <label>
            <span aria-hidden='true'>search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Search by ID, book, requester, or status'
              aria-label='Search print requests'
            />
          </label>
        </div>
        <div className='print-table-wrap'>
          <table className='print-table'>
            <thead>
              <tr>
                <th>Id</th>
                <th>Quantity</th>
                <th>Request Date</th>
                <th>Request By</th>
                <th>Status</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='print-table__empty' colSpan='6'>
                    Loading print requests...
                  </td>
                </tr>
              ) : requests.length ? (
                requests.map((request) => {
                  const status = String(
                    getValue(request, ['status'], 'requested'),
                  ).toLowerCase()
                  const canModify = !['approved', 'printed'].includes(status)
                  const canApprove = ['request', 'requested'].includes(status)
                  const canUnapprove = status === 'approved'
                  return (
                    <tr key={getRequestId(request)}>
                      <td>
                        <strong>#{getRequestId(request)}</strong>
                        <small>{getBookTitle(request)}</small>
                      </td>
                      <td>
                        {Number(
                          getValue(request, ['qty', 'quantity'], 0),
                        ).toLocaleString()}
                      </td>
                      <td>
                        {formatDate(
                          getValue(request, ['request_date', 'requestDate']),
                        )}
                      </td>
                      <td>{getRequesterName(request)}</td>
                      <td>
                        <span
                          className={`print-status print-status--${status}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td>
                        {(canModify || canUnapprove) && (
                          <div className='print-actions'>
                            {canUnapprove && canUnapprovePrint && (
                              <button
                                className='print-actions__unapprove'
                                type='button'
                                onClick={() => setPendingUnapproval(request)}
                              >
                                Unapprove
                              </button>
                            )}
                            {canApprove && canApprovePrint && (
                              <button
                                className='print-actions__approve'
                                type='button'
                                onClick={() => {
                                  setPendingApproval(request)
                                  setApprovalDate(
                                    new Date().toISOString().slice(0, 10),
                                  )
                                  setApprovalComment('')
                                }}
                              >
                                Approve
                              </button>
                            )}
                            {canModify && (canEditPrint || canDeletePrint) && (
                              <>
                                {canEditPrint && (
                                  <button
                                    type='button'
                                    onClick={() =>
                                      setModal({ mode: 'edit', request })
                                    }
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDeletePrint && (
                                  <button
                                    className='print-actions__delete'
                                    type='button'
                                    onClick={() => setPendingDelete(request)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td className='print-table__empty' colSpan='6'>
                    No print requests match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='print-pagination'>
          <span>Showing up to {PRINT_REQUEST_PAGE_SIZE} requests</span>
          <div>
            <button
              type='button'
              onClick={goPrevious}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goNext}
              disabled={!nextCursor || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {modal && (
        <PrintRequestModal
          request={modal.request}
          userId={userId}
          onClose={() => setModal(null)}
          onSaved={refresh}
        />
      )}
      {pendingApproval && (
        <div
          className='print-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isApproving &&
            setPendingApproval(null)
          }
        >
          <section
            className='print-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='approve-print-title'
          >
            <div className='print-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Approval review</span>
                <h2 id='approve-print-title'>Approve print request</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingApproval(null)}
                disabled={isApproving}
                aria-label='Close approval details'
              >
                x
              </button>
            </div>
            <dl className='print-request-details'>
              <div>
                <dt>Request ID</dt>
                <dd>#{getRequestId(pendingApproval)}</dd>
              </div>
              <div>
                <dt>Book</dt>
                <dd>{getBookTitle(pendingApproval)}</dd>
              </div>
              <div>
                <dt>Quantity</dt>
                <dd>
                  {Number(
                    getValue(pendingApproval, ['qty', 'quantity'], 0),
                  ).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Request date</dt>
                <dd>
                  {formatDate(
                    getValue(pendingApproval, ['request_date', 'requestDate']),
                  )}
                </dd>
              </div>
              <div>
                <dt>Requested by</dt>
                <dd>{getRequesterName(pendingApproval)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className='print-status print-status--requested'>
                    requested
                  </span>
                </dd>
              </div>
              <div className='print-request-details__full'>
                <dt>Comment</dt>
                <dd>
                  {getValue(
                    pendingApproval,
                    ['requesting_comment', 'comment', 'remarks'],
                    'No comment',
                  )}
                </dd>
              </div>
            </dl>
            <div className='print-approval-fields'>
              <label>
                Approval date
                <input
                  type='date'
                  value={approvalDate}
                  onChange={(event) => setApprovalDate(event.target.value)}
                  required
                />
              </label>
              <label>
                Approval comment
                <textarea
                  rows='4'
                  value={approvalComment}
                  onChange={(event) => setApprovalComment(event.target.value)}
                  placeholder='Optional approval comment'
                />
              </label>
            </div>
            <div className='print-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingApproval(null)}
                disabled={isApproving}
              >
                Cancel
              </button>
              <button
                className='print-button--approve'
                type='button'
                onClick={confirmApproval}
                disabled={isApproving || !approvalDate}
              >
                {isApproving ? 'Approving...' : 'Approve request'}
              </button>
            </div>
          </section>
        </div>
      )}
      {pendingUnapproval && (
        <div
          className='print-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isUnapproving &&
            setPendingUnapproval(null)
          }
        >
          <section
            className='print-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='unapprove-print-title'
          >
            <div className='print-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Approval review</span>
                <h2 id='unapprove-print-title'>Unapprove print request</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingUnapproval(null)}
                disabled={isUnapproving}
                aria-label='Close unapproval details'
              >
                x
              </button>
            </div>
            <dl className='print-request-details'>
              <div>
                <dt>Request ID</dt>
                <dd>#{getRequestId(pendingUnapproval)}</dd>
              </div>
              <div>
                <dt>Book</dt>
                <dd>{getBookTitle(pendingUnapproval)}</dd>
              </div>
              <div>
                <dt>Quantity</dt>
                <dd>
                  {Number(
                    getValue(pendingUnapproval, ['qty', 'quantity'], 0),
                  ).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Requested by</dt>
                <dd>{getRequesterName(pendingUnapproval)}</dd>
              </div>
              <div className='print-request-details__full'>
                <dt>Current status</dt>
                <dd>
                  <span className='print-status print-status--approved'>
                    approved
                  </span>
                </dd>
              </div>
            </dl>
            <div className='print-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingUnapproval(null)}
                disabled={isUnapproving}
              >
                Cancel
              </button>
              <button
                className='print-button--unapprove'
                type='button'
                onClick={confirmUnapproval}
                disabled={isUnapproving}
              >
                {isUnapproving ? 'Updating...' : 'Set back to requested'}
              </button>
            </div>
          </section>
        </div>
      )}
      {pendingDelete && (
        <div
          className='print-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeleting &&
            setPendingDelete(null)
          }
        >
          <section
            className='print-modal print-modal--confirm'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-print-title'
          >
            <div className='print-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Confirm action</span>
                <h2 id='delete-print-title'>Delete print request?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
                aria-label='Close confirmation'
              >
                x
              </button>
            </div>
            <p>
              Request <strong>#{getRequestId(pendingDelete)}</strong> for{' '}
              {getBookTitle(pendingDelete)} will be permanently deleted.
            </p>
            <div className='print-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className='print-button--danger'
                type='button'
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete request'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default PrintRequest
